import { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import Badge from 'components/partials/badge/badge';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import Heading from 'components/partials/heading/heading';
import Loader from 'components/partials/loader/loader';
import ActionsDropdown from 'components/partials/actions-dropdown';
import NotificationMessage from 'components/partials/notification/notification';
import PowerBiReport from 'components/shared/others/power-bi-report/power-bi-report';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import FavoriteModalForm from '../favorite-reports/modals/favorite-modal-form';
import UpdateModalContent from '../favorite-reports/modals/update-modal-content';

import { useReports } from 'hooks/api/use-reports';
import { useFavoriteReports } from 'hooks/api/use-favorite-reports';

import { readActivePageAndSlicerFilters } from 'utils/powerbi-metadata/powerbi-metadata';
import { comparePageAndFilters } from 'utils/powerbi-metadata/compare-page-and-filters';

import { REPORT_TYPES } from 'constants/reports';

import Icons from 'assets/icons';

import type { PowerBiReportHandle } from 'components/shared/others/power-bi-report/power-bi-report';
import type { PageMetadata } from 'utils/powerbi-metadata/compare-page-and-filters';
import type { ReportTileData } from 'containers/user/reports/report-section/report-tile/report-tile';
import type { FavoriteReportData } from '../favorite-reports/farovite-report-tile/farovite-report-tile';
import type { FavoriteReportFilter } from '../favorite-reports/filter-item/filter-item';

interface ReportPageProps {
  reportId: string | number;
}

interface ReportError {
  status?: number | string;
  statusText?: string;
  [key: string]: unknown;
}

interface OnMetadataPayload {
  activePage: unknown;
  filters: unknown;
}

interface FavoriteMetadataShape {
  filters?: FavoriteReportFilter[];
  [key: string]: unknown;
}

const ReportPage = ({ reportId }: ReportPageProps): React.JSX.Element => {
  const reports = useReports();
  const favoriteReports = useFavoriteReports();
  const [currentReport, setCurrentReport] = useState<
    ReportTileData | undefined
  >();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isBookmarkChanged, setIsBookmarkChanged] = useState<boolean>(false);
  const [capturedBookmarkState, setCapturedBookmarkState] = useState<
    string | null
  >(null);
  const [capturedMetadata, setCapturedMetadata] = useState<PageMetadata | null>(
    null
  );
  const [error, setError] = useState<ReportError | null>(null);
  const [isReportLoaded, setIsReportLoaded] = useState<boolean>(false);
  const powerBiRef = useRef<PowerBiReportHandle>(null);

  const location = useLocation();
  const [searchParams] = useSearchParams();

  const bookmarkId = searchParams.get('bookmarkId');
  const powerbiRelatedReportPageId = (
    location.state as { powerbiRelatedReportPageId?: string } | null
  )?.powerbiRelatedReportPageId;

  const favoriteReportsData = favoriteReports.data as
    | FavoriteReportData[]
    | null
    | undefined;

  const currentFavorite = useMemo<FavoriteReportData | null>(() => {
    if (!bookmarkId || !favoriteReportsData) return null;

    const id = Number(bookmarkId);

    return favoriteReportsData.find((fav) => Number(fav.id) === id) ?? null;
  }, [bookmarkId, favoriteReportsData]);

  const hasFavorites = !!favoriteReportsData && favoriteReportsData.length > 0;
  const isReportPageLoading =
    reports.loading || (favoriteReports.loading && !favoriteReportsData);

  const handleMetadata = (metadata: OnMetadataPayload): void => {
    if (!currentFavorite) return;

    const isEqualMetadata = comparePageAndFilters(
      currentFavorite.metadata as unknown as PageMetadata | null | undefined,
      metadata as unknown as PageMetadata
    );

    setIsBookmarkChanged(!isEqualMetadata);
  };

  useEffect(() => {
    const reportsData = reports.data as ReportTileData[] | null | undefined;

    if (!reportsData) return;

    setCurrentReport(reportsData.find((rpt) => rpt.id === Number(reportId)));
    setIsReportLoaded(false);
  }, [reports.data, reportId]);

  const handleReportError = (err: unknown): void =>
    setError(err as ReportError);
  const handleReportLoaded = (): void => setIsReportLoaded(true);

  const captureCurrentBookmarkState = async (): Promise<string | null> => {
    if (!powerBiRef.current) return null;

    try {
      return await powerBiRef.current.captureBookmark();
    } catch (err) {
      console.error('Failed to capture bookmark state:', err);

      return null;
    }
  };

  const captureBookmarkDataWithMetadata = async (): Promise<{
    bookmarkState: string;
    metadata: PageMetadata | null;
  } | null> => {
    const bookmarkState = await captureCurrentBookmarkState();

    if (!bookmarkState) return null;

    const reportInstance = powerBiRef.current?.reportInstance;
    const metadata = reportInstance
      ? ((await readActivePageAndSlicerFilters(
          reportInstance as Parameters<typeof readActivePageAndSlicerFilters>[0]
        )) as PageMetadata)
      : null;

    return {
      bookmarkState,
      metadata,
    };
  };

  const handleOpenModal = async (
    openModalCallback: () => void
  ): Promise<void> => {
    const data = await captureBookmarkDataWithMetadata();

    if (!data) return;

    setCapturedBookmarkState(data.bookmarkState);
    setCapturedMetadata(data.metadata);
    openModalCallback();
  };

  const handleOpenUpdateModal = (): Promise<void> =>
    handleOpenModal(() => setIsUpdateModalOpen(true));

  const handleOpenFavoriteModal = (): Promise<void> =>
    handleOpenModal(() => setIsCreateModalOpen(true));

  const handleCloseUpdateModal = (): void => {
    setIsUpdateModalOpen(false);
    setCapturedBookmarkState(null);
  };

  const handleCloseCreateModal = (): void => {
    setIsCreateModalOpen(false);
    setCapturedBookmarkState(null);
    setCapturedMetadata(null);
  };

  return (
    <ResponsiveWrapper>
      <Breadcrumbs
        breadcrumbs={[
          { label: 'Reports', href: '/reports/all' },
          { label: currentReport?.title ?? '', isActive: true },
        ]}
      />
      <div className="flex justify-between items-center mt-4">
        <div className="flex items-center gap-2">
          <Heading customClass="mr-2" variant="h1">
            {currentReport?.title}
          </Heading>

          {currentFavorite && !isBookmarkChanged && (
            <Badge
              text={currentFavorite.name}
              variant="primary"
              icon={<Icons.Star customClass="w-3.5 h-3.5" />}
            />
          )}
        </div>

        <ActionsDropdown
          isDisabled={!isReportLoaded}
          buttonName="Save to favorites"
          startIcon={<Icons.Star customClass="w-5 h-5" />}
          options={[
            {
              label: 'Save as new',
              startIcon: <Icons.Plus />,
              onClick: () => {
                void handleOpenFavoriteModal();
              },
            },
            {
              label: 'Update existing',
              startIcon: <Icons.Rotate />,
              onClick: () => {
                void handleOpenUpdateModal();
              },
              isDisabled: !hasFavorites,
            },
          ]}
        />
      </div>
      {error && (
        <div className="mt-6">
          <NotificationMessage
            customClass="mt-6"
            heading="Error loading report"
            variant="error"
            message={`There was an error loading this report. (${error?.status} ${error?.statusText})`}
          />
        </div>
      )}

      <div className="mt-6 min-h-[300px] relative">
        {isReportPageLoading ? (
          <Loader loading />
        ) : currentReport ? (
          <PowerBiReport
            ref={powerBiRef}
            reportId={currentReport.powerBiId!}
            relatedPageId={powerbiRelatedReportPageId}
            fitToWidth={
              currentReport.productType === REPORT_TYPES.FINANCIAL_AID_VIEW
            }
            onError={handleReportError}
            onReportLoaded={handleReportLoaded}
            bookmarkState={currentFavorite?.bookmarkState || undefined}
            onMetadata={handleMetadata}
          />
        ) : (
          <NotificationMessage
            customClass="mt-6"
            heading="Unavailable"
            variant="info"
            message="No report found"
          />
        )}
      </div>

      {isUpdateModalOpen && (
        <UpdateModalContent
          onClose={handleCloseUpdateModal}
          bookmarkState={capturedBookmarkState}
          metadata={capturedMetadata as unknown as FavoriteMetadataShape | null}
          reportId={reportId}
        />
      )}

      {isCreateModalOpen && (
        <FavoriteModalForm
          reportId={reportId}
          bookmarkState={capturedBookmarkState}
          metadata={capturedMetadata as unknown as FavoriteMetadataShape | null}
          onClose={handleCloseCreateModal}
        />
      )}
    </ResponsiveWrapper>
  );
};

export default ReportPage;
